import React from "react";
import {css} from "@emotion/css";
import {Icon, Text, styled, theme} from "@reside/ui";

type BaseProps = Readonly<{
  total: number;
  className?: string;
  testId?: string;
}>;

type PageVariantProps = BaseProps &
  Readonly<{
    variant?: "page";
    /** 1-based page index */
    current: number;
    pageSize: number;
    onChange: (page: number) => void;
  }>;

type LoadMoreVariantProps = BaseProps &
  Readonly<{
    variant: "load-more";
    loadedCount: number;
    hasMore: boolean;
    onLoadMore: () => void;
  }>;

export type PagePaginationProps = PageVariantProps | LoadMoreVariantProps;

export const getPageRange = (
  current: number,
  pageSize: number,
  total: number,
): Readonly<{start: number; end: number}> => {
  if (total === 0) {
    return {start: 0, end: 0};
  }

  const start = (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, total);

  return {start, end};
};

export const formatPageRangeLabel = (
  current: number,
  pageSize: number,
  total: number,
): string => {
  const {start, end} = getPageRange(current, pageSize, total);

  return `${start}-${end} of ${total}`;
};

export const PagePagination = (props: PagePaginationProps) => {
  const {total, className, testId = "pagePagination"} = props;

  if (total === 0) {
    return null;
  }

  const isLoadMore = props.variant === "load-more";
  const label = isLoadMore
    ? `1-${props.loadedCount} of ${total}`
    : formatPageRangeLabel(props.current, props.pageSize, total);

  const hasPrevious = isLoadMore
    ? false
    : props.current > 1;
  const hasNext = isLoadMore
    ? props.hasMore
    : props.current * props.pageSize < total;

  const goPrevious = () => {
    if (!isLoadMore && hasPrevious) {
      props.onChange(props.current - 1);
    }
  };

  const goNext = () => {
    if (isLoadMore) {
      if (hasNext) {
        props.onLoadMore();
      }

      return;
    }

    if (hasNext) {
      props.onChange(props.current + 1);
    }
  };

  return (
    <Root className={className} data-test-id={testId}>
      {!isLoadMore && (
        <NavButton
          aria-label="Previous page"
          data-test-id={`${testId}-previous`}
          disabled={!hasPrevious}
          type="button"
          onClick={goPrevious}
        >
          <Icon
            name="left"
            color={hasPrevious ? theme.color.darkBlue100 : theme.color.gray100}
            size={10}
          />
        </NavButton>
      )}
      <Label fontWeight="medium" color="darkBlue100">
        {label}
      </Label>
      <NavButton
        aria-label={isLoadMore ? "Load more" : "Next page"}
        data-test-id={`${testId}-next`}
        disabled={!hasNext}
        type="button"
        onClick={goNext}
      >
        <Icon
          name="right"
          color={hasNext ? theme.color.darkBlue100 : theme.color.gray100}
          size={10}
        />
      </NavButton>
    </Root>
  );
};

const Root = styled.div`
  align-items: center;
  display: inline-flex;
  gap: 12px;
`;

const Label = styled(Text)`
  font-size: 14px;
  line-height: 20px;
  min-width: 96px;
  text-align: center;
  white-space: nowrap;
`;

const NavButton = styled.button`
  align-items: center;
  background: transparent;
  border: 1px solid ${theme.color.gray100};
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  height: 28px;
  justify-content: center;
  padding: 0;
  width: 28px;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }

  &:not(:disabled):hover {
    border-color: ${theme.color.primary};
  }
`;

export const pagePaginationAlignRight = css`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`;
